// src/components/seasonal/August2026.js
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { logEvent } from 'firebase/analytics';
import { analytics, functionsInstance } from '../../firebase/firebase';
import { httpsCallable } from 'firebase/functions';
import TrialPromptPopup from './TrialPromptPopup';

const HOLIDAY_TAG = 'august-2026';

const August2026 = () => {
  const hasLoggedRead = useRef(false);
  const [email, setEmail]               = useState('');
  const [gdprChecked, setGdprChecked]   = useState(false);
  const [submitState, setSubmitState]   = useState('idle');
  const [errorMsg, setErrorMsg]         = useState('');
  const [showTrialPopup, setShowTrialPopup] = useState(false);

  useEffect(() => {
    document.title = "100 Top TPT Keywords for August School Prep 2026 | Mr Joel's Seller Tools";
    window.scrollTo(0, 0);
    logEvent(analytics, 'page_view', {
      page_title: 'August Keyword Report Signup',
      page_path: '/keywords/august-2026'
    });
    const handleScroll = () => {
      if (window.scrollY > 300 && !hasLoggedRead.current) {
        logEvent(analytics, 'august_read_content');
        hasLoggedRead.current = true;
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!gdprChecked) return;
    setSubmitState('loading');
    setErrorMsg('');
    try {
      logEvent(analytics, 'august_signup_submit');
      const subscribeToReport = httpsCallable(functionsInstance, 'subscribeToSeasonalReport');
      const result = await subscribeToReport({ email, tag: HOLIDAY_TAG });
      const { userType } = result.data;
      if (userType === 'existing') {
        setSubmitState('success-existing');
        logEvent(analytics, 'august_existing_user');
      } else {
        setSubmitState('success-new');
        logEvent(analytics, 'august_new_subscriber');
        setTimeout(() => setShowTrialPopup(true), 5000);
      }
    } catch (err) {
      console.error('Signup error:', err);
      setErrorMsg('Something went wrong — please try again or email info@mrjoelstptgenerator.com');
      setSubmitState('error');
    }
  };

  const scrollToForm = (e) => {
    e.preventDefault();
    const input = document.getElementById('aug-email');
    if (input) { input.focus(); input.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
  };

  // Tier lookup helpers (shared with the report email)
  const DEMAND = {
    massive: { emoji: '🚀', label: 'Massive',   color: '#dc2626' },
    veryHigh:{ emoji: '🔥', label: 'Very High', color: '#ea580c' },
    high:    { emoji: '⭐', label: 'High',      color: '#1d4ed8' },
    solid:   { emoji: '📈', label: 'Solid',     color: '#16a34a' },
    steady:  { emoji: '💫', label: 'Steady',    color: '#7c3aed' },
  };
  const COMP = {
    open:      { emoji: '🟢', label: 'Open Field', bg: '#dcfce7', fg: '#166534' },
    veryLow:   { emoji: '🟡', label: 'Very Low',   bg: '#fef9c3', fg: '#854d0e' },
    moderate:  { emoji: '🟠', label: 'Moderate',   bg: '#fed7aa', fg: '#9a3412' },
    contested: { emoji: '🔶', label: 'Contested',  bg: '#ffedd5', fg: '#c2410c' },
    saturated: { emoji: '🔴', label: 'Saturated',  bg: '#fee2e2', fg: '#991b1b' },
  };

  // Top 20 keywords — reshuffled so gems interleave with scored picks
  const KEYWORDS = [
    { rank: 1,  kw: 'team building tetris',                       note: '🏅 Wildest ratio in the database — very high demand, open field',   score: 100, demand: DEMAND.veryHigh, comp: COMP.open,      tier: 'gold',   capped: true  },
    { rank: 2,  kw: 'back to school starburst tag',               note: '🔥 Highest opportunity — massive demand meets very low competition', score: 100, demand: DEMAND.massive,  comp: COMP.veryLow,   tier: 'gold',   capped: true  },
    { rank: 3,  kw: 'racecar reward chart',                       note: '💎 Classroom management gold — theme reward niche, open field',      score: null, demand: DEMAND.high,     comp: COMP.open,      tier: 'gem',    capped: false },
    { rank: 4,  kw: 'first day of school 7th grade',              note: '🎒 Grade-specific = ~10× better ranking odds',                       score: 100, demand: DEMAND.massive,  comp: COMP.saturated, tier: 'gold',   capped: true  },
    { rank: 5,  kw: 'desert island ice breaker',                  note: '💎 Best opportunity ratio in ice breakers — open field',             score: null, demand: DEMAND.solid,    comp: COMP.open,      tier: 'gem',    capped: false },
    // Blurred rows (6-8)
    { rank: 6,  kw: 'meet the teacher',                           note: '📈 MTT surge is happening this week',                                score: 27,  demand: DEMAND.massive,  comp: COMP.saturated, tier: 'strong', capped: false },
    { rank: 7,  kw: 'aleks math placement test',                  note: '💎 September placement testing sweet spot',                          score: null, demand: DEMAND.solid,    comp: COMP.open,      tier: 'gem',    capped: false },
    { rank: 8,  kw: 'llama door decorations',                     note: '💎 Theme décor niche — high demand, open field',                     score: null, demand: DEMAND.high,     comp: COMP.open,      tier: 'gem',    capped: false },
    // Hidden in report (9-20)
    { rank: 9,  kw: 'plant and animal cells test',                note: '🧬 September curriculum prep — climbing 200%+',                      score: 26,  demand: DEMAND.massive,  comp: COMP.moderate,  tier: 'strong', capped: false },
    { rank: 10, kw: 'back to school',                             note: '📈 Peak search window — massive but broad',                          score: 44,  demand: DEMAND.massive,  comp: COMP.saturated, tier: 'strong', capped: false },
    { rank: 11, kw: 'talking reward chart selective mutism',      note: '💎 SEL specialty niche — open field',                                score: null, demand: DEMAND.solid,    comp: COMP.open,      tier: 'gem',    capped: false },
    { rank: 12, kw: 'welcome back powerpoints',                   note: '💎 Premium editable PPT pricing — very low competition',             score: null, demand: DEMAND.solid,    comp: COMP.veryLow,   tier: 'gem',    capped: false },
    { rank: 13, kw: 'all about me',                               note: '📝 Huge but saturated — narrow by grade for rank',                   score: 24,  demand: DEMAND.veryHigh, comp: COMP.saturated, tier: 'strong', capped: false },
    { rank: 14, kw: 'google slides congratulations background',   note: '💎 Digital classroom staple — open field',                           score: null, demand: DEMAND.solid,    comp: COMP.open,      tier: 'gem',    capped: false },
    { rank: 15, kw: 'coding bulletin boards',                     note: '💎 STEM classroom niche — very low competition',                     score: null, demand: DEMAND.solid,    comp: COMP.veryLow,   tier: 'gem',    capped: false },
    { rank: 16, kw: 'classroom decor bundle',                     note: '📦 Bundle = higher cart value',                                      score: 20,  demand: DEMAND.veryHigh, comp: COMP.saturated, tier: 'strong', capped: false },
    { rank: 17, kw: 'classroom decor high school english',        note: '💎 Niche by subject + level = massive edge',                         score: null, demand: DEMAND.solid,    comp: COMP.veryLow,   tier: 'gem',    capped: false },
    { rank: 18, kw: 'welcome back bulletin board idea',           note: '💎 Quick win décor — very low competition',                          score: null, demand: DEMAND.solid,    comp: COMP.veryLow,   tier: 'gem',    capped: false },
    { rank: 19, kw: 'beginning of the year papers',               note: '💎 Forms + handouts niche',                                          score: null, demand: DEMAND.solid,    comp: COMP.veryLow,   tier: 'gem',    capped: false },
    { rank: 20, kw: 'emergency sub plans high school leadership', note: '💎 Extreme HS sub niche — open field',                               score: null, demand: DEMAND.steady,   comp: COMP.open,      tier: 'gem',    capped: false },
  ];

  const visibleRows  = KEYWORDS.slice(0, 5);
  const blurredRows  = KEYWORDS.slice(5, 8);

  const getScoreBadge = (kw) => {
    if (kw.tier === 'gem') return (
      <span style={{background: '#92400e', color: '#fbbf24', fontSize: '11px', fontWeight: 800, padding: '4px 10px', borderRadius: '20px', whiteSpace: 'nowrap'}}>💎 GEM</span>
    );
    const bg    = kw.tier === 'gold' ? '#eff6ff' : kw.tier === 'strong' ? '#eff6ff' : 'white';
    const border= kw.tier === 'gold' ? '#1a56db' : kw.tier === 'strong' ? '#93c5fd' : '#e2e8f0';
    const color = kw.tier === 'gold' ? '#1e40af' : kw.tier === 'strong' ? '#1d4ed8' : '#64748b';
    return (
      <span style={{background: bg, border: `1px solid ${border}`, color, fontSize: '13px', fontWeight: 800, padding: '4px 12px', borderRadius: '20px', whiteSpace: 'nowrap', fontFamily: 'Sora, sans-serif'}}>
        ★ {kw.score}{kw.capped ? '*' : ''}
      </span>
    );
  };

  const getDemandBadge = (d) => (
    <span style={{color: d.color, fontSize: '12px', fontWeight: 800, whiteSpace: 'nowrap', fontFamily: 'Sora, sans-serif'}}>
      {d.emoji} {d.label}
    </span>
  );

  const getCompBadge = (c) => (
    <span style={{background: c.bg, color: c.fg, fontSize: '11px', fontWeight: 800, padding: '3px 10px', borderRadius: '12px', whiteSpace: 'nowrap', fontFamily: 'Sora, sans-serif'}}>
      {c.emoji} {c.label}
    </span>
  );

  return (
    <div className="aug-wrapper">

      {showTrialPopup && (
        <TrialPromptPopup
          onClose={() => setShowTrialPopup(false)}
          accentColor="#1a56db"
          accentDark="#1e3a8a"
          accentLight="#eff6ff"
          accentBorder="#dbeafe"
        />
      )}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800;900&family=DM+Sans:ital,wght@0,400;0,500;1,400&display=swap');

        .aug-wrapper {
          --blue:        #1a56db;
          --blue-dark:   #1e3a8a;
          --blue-light:  #eff6ff;
          --blue-border: #dbeafe;
          --red:         #dc2626;
          --red-light:   #fef2f2;
          --gold:        #fbbf24;
          --gold-dark:   #f59e0b;
          --gold-bg:     #fffbeb;
          --green:       #16a34a;
          --green-dark:  #14532d;
          --green-light: #f0fdf4;
          --green-border:#bbf7d0;
          --text:        #0f172a;
          --muted:       #64748b;
          --border:      #e2e8f0;
          --bg:          #f8fafc;
          font-family: 'DM Sans', sans-serif;
          background: var(--bg);
          color: var(--text);
          min-height: 100vh;
          overflow-x: hidden;
        }

        .aug-nav {
          display: flex; align-items: center; justify-content: space-between;
          padding: 16px 32px; background: white;
          border-bottom: 1px solid var(--border);
          position: sticky; top: 0; z-index: 100;
        }
        .aug-nav-logo { display: flex; align-items: center; gap: 10px; text-decoration: none; }
        .aug-nav-mark {
          width: 36px; height: 36px;
          background: linear-gradient(135deg, var(--blue), var(--blue-dark));
          border-radius: 9px; display: flex; align-items: center; justify-content: center;
          font-family: 'Sora', sans-serif; font-weight: 900; font-size: 14px; color: white;
        }
        .aug-nav-text { font-family: 'Sora', sans-serif; font-weight: 700; font-size: 14px; color: var(--text); }
        .aug-nav-sub  { font-size: 11px; color: var(--muted); }
        .aug-nav-cta  { font-family: 'Sora', sans-serif; font-size: 13px; font-weight: 600; color: var(--blue); text-decoration: none; border: 1px solid var(--blue-border); padding: 7px 16px; border-radius: 8px; transition: background 0.15s; }
        .aug-nav-cta:hover { background: var(--blue-light); }

        .aug-urgency { background: linear-gradient(135deg, var(--blue-dark), var(--blue), #0369a1); color: white; text-align: center; padding: 10px 24px; font-size: 14px; font-weight: 600; }

        .aug-hero { max-width: 1080px; margin: 0 auto; padding: 60px 24px 40px; text-align: center; }
        .aug-badge { display: inline-flex; align-items: center; gap: 7px; background: var(--blue-light); border: 1px solid var(--blue-border); color: var(--blue-dark); font-size: 12px; font-weight: 700; padding: 6px 16px; border-radius: 20px; margin-bottom: 24px; letter-spacing: 0.03em; text-transform: uppercase; }
        .aug-badge-dot { width: 6px; height: 6px; background: var(--blue); border-radius: 50%; animation: pulse 2s infinite; }
        @keyframes pulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.5; transform: scale(1.4); } }

        .aug-wrapper h1, .sora { font-family: 'Sora', sans-serif; }
        .aug-title { font-weight: 900; font-size: clamp(30px, 5vw, 54px); line-height: 1.1; letter-spacing: -0.03em; color: var(--text); margin-bottom: 20px; }
        .aug-title .blue-accent { background: linear-gradient(135deg, var(--blue), var(--blue-dark)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
        .aug-title .gold { color: #d97706; -webkit-text-fill-color: #d97706; }
        .aug-sub { font-size: 18px; color: var(--muted); line-height: 1.7; margin-bottom: 40px; max-width: 800px; margin-left: auto; margin-right: auto; }
        .aug-sub strong { color: var(--text); }

        .aug-grid { max-width: 1080px; margin: 0 auto; padding: 0 24px 60px; display: grid; grid-template-columns: 1fr 420px; gap: 40px; align-items: stretch; }
        .aug-info-card, .aug-form-card { background: white; border: 1px solid var(--border); border-radius: 24px; padding: 32px; box-shadow: 0 10px 40px rgba(0,0,0,0.06); box-sizing: border-box; }
        .aug-form-card { position: sticky; top: 100px; }
        .aug-section-label { font-size: 11px; font-weight: 700; color: var(--blue); text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 12px; }
        .aug-card-heading { font-weight: 800; font-size: 22px; color: var(--text); margin-bottom: 24px; line-height: 1.2; }

        .aug-perks-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
        .aug-perk { display: flex; align-items: center; gap: 10px; background: var(--bg); border: 1px solid var(--border); border-radius: 12px; padding: 14px 16px; font-size: 13px; font-weight: 500; color: var(--text); }

        .aug-field-label { display: block; font-size: 13px; font-weight: 600; color: #374151; margin-bottom: 6px; }
        .aug-field-input { width: 100%; padding: 14px; border: 1.5px solid var(--border); border-radius: 12px; font-size: 15px; color: var(--text); font-family: 'DM Sans', sans-serif; outline: none; margin-bottom: 20px; background: #f8fafc; transition: all 0.2s; box-sizing: border-box; }
        .aug-field-input:focus { border-color: var(--blue); background: white; }
        .aug-gdpr-box { background: #f8fafc; border: 1px solid var(--border); border-radius: 12px; padding: 14px; margin-bottom: 20px; }
        .aug-gdpr-label { display: flex; gap: 10px; cursor: pointer; }
        .aug-gdpr-checkbox { width: 16px; height: 16px; flex-shrink: 0; margin-top: 3px; accent-color: var(--blue); }
        .aug-gdpr-text { font-size: 12px; color: var(--muted); line-height: 1.5; }
        .aug-gdpr-text a { color: var(--blue); }
        .aug-submit-btn { width: 100%; background: linear-gradient(135deg, var(--blue), var(--blue-dark)); color: white; font-weight: 800; font-size: 15px; padding: 16px; border: none; border-radius: 12px; cursor: pointer; box-shadow: 0 4px 14px rgba(26,86,219,0.3); transition: transform 0.15s; font-family: 'Sora', sans-serif; }
        .aug-submit-btn:hover { transform: translateY(-1px); }
        .aug-amazon-badge { background: var(--gold-bg); border: 1px solid #fde68a; color: #92400e; font-size: 12px; padding: 12px; border-radius: 10px; margin-top: 16px; text-align: center; }

        .aug-preview-row { display: grid; grid-template-columns: 32px 1fr 100px 110px 120px; gap: 0; padding: 13px 32px; border-bottom: 1px solid #f1f5f9; align-items: center; }
        .aug-app-promo { max-width: 1080px; margin: 0 auto; padding: 0 24px 40px; }

        @media (max-width: 850px) {
          .aug-grid { grid-template-columns: 1fr; }
          .aug-form-card { position: static; order: -1; }
          .aug-perks-grid { grid-template-columns: 1fr; }
          .aug-title { font-size: 30px; }
          .aug-preview-row { grid-template-columns: 24px 1fr 80px; padding: 12px 16px; }
          .aug-hide-mobile { display: none; }
          .aug-nav { padding: 12px 16px; }
        }
      `}</style>

      {/* URGENCY BANNER */}
      <div className="aug-urgency">
        🎒 Peak August school-prep window · Data from 129,000+ TPT searches · Analysed August 5th 2026
      </div>

      {/* NAV */}
      <nav className="aug-nav">
        <Link to="/" className="aug-nav-logo">
          <div className="aug-nav-mark">MJ</div>
          <div>
            <div className="aug-nav-text">Mr Joel's Seller Tools</div>
            <div className="aug-nav-sub">TPT Keyword Intelligence</div>
          </div>
        </Link>
        <Link to="/app" className="aug-nav-cta">Try the App Free →</Link>
      </nav>

      {/* HERO */}
      <div className="aug-hero">
        <div className="aug-badge">
          <div className="aug-badge-dot"></div>
          Analysed August 5th 2026 · Free Keyword Report
        </div>
        <h1 className="aug-title sora">
          100 Top Niche TPT Keywords<br />
          <span className="blue-accent">for August School Prep</span><br />
          <span className="gold">— Ranked by Opportunity</span>
        </h1>
        <p className="aug-sub">
          Teachers are prepping classrooms and stocking curriculum this week. We just analysed <strong>129,000+ TPT searches</strong> to find the top niche opportunities across <strong>10 core August categories</strong> — from BTS core through September curriculum prep.
          Get the full ranked report — <strong>free, direct to your inbox</strong>.
        </p>
      </div>

      {/* MAIN 2-COL LAYOUT */}
      <div className="aug-grid">

        {/* LEFT — Info Card */}
        <div className="aug-info-card">
          <div className="aug-section-label">What You're Getting</div>
          <h2 className="aug-card-heading sora">Fresh data. Real opportunity.</h2>

          <p style={{fontSize: '15px', color: '#475569', lineHeight: 1.7, marginBottom: '24px'}}>
            We analysed <strong style={{color: '#0f172a'}}>129,000+ TPT searches</strong> on August 5th 2026 to
            find the best August school-prep keyword opportunities — filtered to niche + performing picks
            with no brands or off-season noise.
            <br /><br />
            Includes <strong style={{color: '#0f172a'}}>100 keywords across 10 categories</strong>: BTS core,
            meet the teacher, classroom setup + décor, classroom management, get-to-know-you, sub plans,
            September reading + math prep, digital resources, and book studies.
          </p>

          <div className="aug-perks-grid" style={{marginBottom: '24px'}}>
            <div className="aug-perk"><span style={{color: '#1a56db', fontSize: '16px'}}>⭐</span> Opportunity Rankings</div>
            <div className="aug-perk"><span style={{color: '#16a34a', fontSize: '16px'}}>📈</span> Demand Tier Badges</div>
            <div className="aug-perk"><span style={{color: '#f59e0b', fontSize: '16px'}}>🏆</span> Competition Tiers</div>
            <div className="aug-perk"><span style={{color: '#8b5cf6', fontSize: '16px'}}>📅</span> Trend vs May 2026</div>
            <div className="aug-perk"><span style={{color: '#ec4899', fontSize: '16px'}}>💎</span> Hidden Gem Picks</div>
            <div className="aug-perk"><span style={{color: '#06b6d4', fontSize: '16px'}}>🎁</span> $25 Amazon Draw Entry</div>
          </div>

          <div style={{background: '#eff6ff', border: '1px solid #dbeafe', borderRadius: '12px', padding: '16px'}}>
            <div style={{fontSize: '13px', fontWeight: 700, color: '#1e3a8a', marginBottom: '4px'}}>💡 The ranking window is closing</div>
            <div style={{fontSize: '13px', color: '#1d4ed8', lineHeight: 1.6}}>
              Teachers shop hardest right now. TPT's algorithm rewards products with existing rank
              history when searches spike — publish this week, not the week school starts.
            </div>
          </div>
        </div>

        {/* RIGHT — Sticky Form Card */}
        <div className="aug-form-card">
          <div className="aug-section-label">Free · No Card Needed</div>
          <h2 className="aug-card-heading sora">Get the August Keyword Report</h2>

          {/* SUCCESS: NEW */}
          {submitState === 'success-new' && (
            <div style={{textAlign: 'center', padding: '16px 0'}}>
              <div style={{fontSize: '48px', marginBottom: '16px'}}>🎒</div>
              <div className="sora" style={{fontWeight: 800, fontSize: '20px', color: '#0f172a', marginBottom: '8px'}}>You're in!</div>
              <div style={{fontSize: '14px', color: '#64748b', lineHeight: 1.6, marginBottom: '20px'}}>
                The August keyword report is on its way to <strong style={{color: '#0f172a'}}>{email}</strong>.<br/>
                You're also entered into the monthly $25 Amazon draw. 🎁
              </div>
              <div style={{background: '#eff6ff', border: '1px solid #dbeafe', borderRadius: '12px', padding: '16px', fontSize: '13px', color: '#1e40af', marginBottom: '16px'}}>
                <strong>While you wait —</strong> try the full app free for 1 hour and search any keyword yourself.
              </div>
              <Link to="/app" className="aug-submit-btn sora" style={{display: 'block', textAlign: 'center', textDecoration: 'none', lineHeight: '50px', padding: '0 16px'}}>
                Try the App Free →
              </Link>
            </div>
          )}

          {/* SUCCESS: EXISTING */}
          {submitState === 'success-existing' && (
            <div style={{textAlign: 'center', padding: '16px 0'}}>
              <div style={{fontSize: '48px', marginBottom: '16px'}}>👋</div>
              <div className="sora" style={{fontWeight: 800, fontSize: '20px', color: '#0f172a', marginBottom: '8px'}}>We recognise you!</div>
              <div style={{fontSize: '14px', color: '#64748b', lineHeight: 1.6, marginBottom: '20px'}}>
                Looks like <strong style={{color: '#0f172a'}}>{email}</strong> has signed up with us before — don't worry, you're all set. 🎒<br/><br/>
                <strong>Check your inbox</strong> — the August keyword report is on its way right now!
              </div>
              <div style={{background: '#eff6ff', border: '1px solid #dbeafe', borderRadius: '12px', padding: '16px', fontSize: '13px', color: '#1e40af', marginBottom: '16px'}}>
                Can't find it? Check your spam folder or search for <strong>"Mr Joel"</strong> in your inbox.
              </div>
              <Link to="/app" className="aug-submit-btn sora" style={{display: 'block', textAlign: 'center', textDecoration: 'none', lineHeight: '50px', padding: '0 16px'}}>
                Go to the App →
              </Link>
            </div>
          )}

          {/* FORM */}
          {(submitState === 'idle' || submitState === 'loading' || submitState === 'error') && (
            <>
              <div style={{fontSize: '14px', color: '#64748b', marginBottom: '24px', lineHeight: 1.5}}>
                Get the full 100-keyword report — free, no card needed, unsubscribe any time.
              </div>
              <form onSubmit={handleFormSubmit}>
                <label className="aug-field-label" htmlFor="aug-email">Email Address *</label>
                <input className="aug-field-input" type="email" id="aug-email" placeholder="yourname@email.com" value={email} onChange={(e) => setEmail(e.target.value)} required disabled={submitState === 'loading'} />
                <div className="aug-gdpr-box">
                  <label className="aug-gdpr-label">
                    <input type="checkbox" className="aug-gdpr-checkbox" checked={gdprChecked} onChange={(e) => setGdprChecked(e.target.checked)} required disabled={submitState === 'loading'} />
                    <span className="aug-gdpr-text">
                      <strong>Yes, send me the August keyword report.</strong><br />
                      Unsubscribe any time. We use Mailchimp. <a href="https://mailchimp.com/legal/terms" target="_blank" rel="noopener noreferrer">Privacy policy →</a>
                    </span>
                  </label>
                </div>
                {submitState === 'error' && (
                  <div style={{background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', padding: '12px 14px', marginBottom: '16px', fontSize: '13px', color: '#dc2626'}}>{errorMsg}</div>
                )}
                <button type="submit" className="aug-submit-btn" disabled={submitState === 'loading' || !gdprChecked} style={{opacity: submitState === 'loading' ? 0.7 : 1, cursor: submitState === 'loading' ? 'not-allowed' : 'pointer'}}>
                  {submitState === 'loading' ? 'Sending...' : 'Send Me the Report →'}
                </button>
                <div className="aug-amazon-badge">🎁 <strong>Automatic Entry:</strong> Monthly $25 Amazon voucher draw</div>
                <div style={{textAlign: 'center', marginTop: '12px', fontSize: '11px', color: '#94a3b8'}}>🔒 No spam · Free forever · One email with your report</div>
              </form>
            </>
          )}
        </div>
      </div>

      {/* PREVIEW TABLE */}
      <div style={{maxWidth: '1080px', margin: '0 auto', padding: '0 24px 40px'}}>
        <div style={{background: 'white', border: '1px solid #e2e8f0', borderRadius: '24px', overflow: 'hidden', boxShadow: '0 10px 40px rgba(0,0,0,0.06)'}}>

          {/* Table Header */}
          <div style={{background: 'linear-gradient(135deg, #1e3a8a, #1a56db)', padding: '32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px'}}>
            <div>
              <div style={{fontSize: '11px', fontWeight: 700, color: '#bfdbfe', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px'}}>🎒 Sneak Peek — August 2026 Report</div>
              <div className="sora" style={{fontWeight: 800, fontSize: '24px', color: 'white'}}>Top August school-prep keywords right now</div>
              <div style={{fontSize: '14px', color: '#bfdbfe', marginTop: '6px'}}>Analysed August 5th 2026 · Sign up above to get all 100 free</div>
            </div>
            <div style={{display: 'flex', gap: '12px'}}>
              <div style={{textAlign: 'center', background: 'rgba(255,255,255,0.1)', padding: '12px 20px', borderRadius: '12px'}}>
                <div className="sora" style={{fontWeight: 900, fontSize: '24px', color: '#fbbf24'}}>100</div>
                <div style={{fontSize: '11px', color: '#bfdbfe', textTransform: 'uppercase', marginTop: '4px'}}>Keywords</div>
              </div>
              <div style={{textAlign: 'center', background: 'rgba(255,255,255,0.1)', padding: '12px 20px', borderRadius: '12px'}}>
                <div className="sora" style={{fontWeight: 900, fontSize: '24px', color: '#93c5fd'}}>10</div>
                <div style={{fontSize: '11px', color: '#bfdbfe', textTransform: 'uppercase', marginTop: '4px'}}>Categories</div>
              </div>
            </div>
          </div>

          {/* Legend */}
          <div style={{background: '#f8fafc', padding: '16px 32px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap'}}>
            <div style={{fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em'}}>Score guide:</div>
            <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}><span className="sora" style={{background: '#eff6ff', border: '1px solid #1a56db', color: '#1e40af', fontSize: '11px', fontWeight: 700, padding: '4px 10px', borderRadius: '12px'}}>★ 70+</span><span style={{fontSize: '13px', color: '#64748b', fontWeight: 500}}>Gold Mine</span></div>
            <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}><span className="sora" style={{background: '#eff6ff', border: '1px solid #93c5fd', color: '#1d4ed8', fontSize: '11px', fontWeight: 700, padding: '4px 10px', borderRadius: '12px'}}>★ 20–69</span><span style={{fontSize: '13px', color: '#64748b', fontWeight: 500}}>Strong</span></div>
            <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}><span className="sora" style={{background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', fontSize: '11px', fontWeight: 700, padding: '4px 10px', borderRadius: '12px'}}>💎 GEM</span><span style={{fontSize: '13px', color: '#64748b', fontWeight: 500}}>Hidden Gem</span></div>
          </div>

          {/* Column Headers */}
          <div style={{display: 'grid', gridTemplateColumns: '32px 1fr 100px 110px 120px', gap: 0, padding: '14px 32px', background: '#f1f5f9', borderBottom: '1px solid #e2e8f0'}}>
            {['#', 'Keyword', 'Score', 'Demand', 'Competition'].map((h, i) => (
              <div key={h} style={{fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', textAlign: i > 1 ? 'center' : 'left'}}>{h}</div>
            ))}
          </div>

          {/* Visible rows 1-5 */}
          <div>
            {visibleRows.map((kw) => (
              <div key={kw.rank} className="aug-preview-row" style={{background: kw.rank % 2 === 0 ? '#fafafa' : 'white'}}>
                <div className="sora" style={{fontSize: '13px', fontWeight: 700, color: '#cbd5e1'}}>{kw.rank}</div>
                <div>
                  <div style={{fontSize: '15px', fontWeight: 700, color: '#0f172a'}}>{kw.kw}</div>
                  <div style={{fontSize: '12px', color: '#64748b', marginTop: '2px'}}>{kw.note}</div>
                </div>
                <div style={{textAlign: 'center'}}>{getScoreBadge(kw)}</div>
                <div className="aug-hide-mobile" style={{textAlign: 'center'}}>{getDemandBadge(kw.demand)}</div>
                <div className="aug-hide-mobile" style={{textAlign: 'center'}}>{getCompBadge(kw.comp)}</div>
              </div>
            ))}

            {/* Blurred rows 6-8 */}
            <div style={{position: 'relative'}}>
              <div style={{filter: 'blur(5px)', pointerEvents: 'none', opacity: 0.6}}>
                {blurredRows.map((kw) => (
                  <div key={kw.rank} className="aug-preview-row">
                    <div className="sora" style={{fontSize: '13px', fontWeight: 700, color: '#cbd5e1'}}>{kw.rank}</div>
                    <div>
                      <div style={{fontSize: '15px', fontWeight: 700, color: '#0f172a'}}>{kw.kw}</div>
                      <div style={{fontSize: '12px', color: '#64748b'}}>{kw.note}</div>
                    </div>
                    <div style={{textAlign: 'center'}}>{getScoreBadge(kw)}</div>
                    <div style={{textAlign: 'center'}}>{getDemandBadge(kw.demand)}</div>
                    <div style={{textAlign: 'center'}}>{getCompBadge(kw.comp)}</div>
                  </div>
                ))}
              </div>

              {/* Blur CTA overlay */}
              <div style={{position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(to bottom, rgba(248,250,252,0) 0%, rgba(248,250,252,0.9) 40%, rgba(248,250,252,1) 100%)'}}>
                <div style={{textAlign: 'center', padding: '30px', background: 'white', borderRadius: '20px', boxShadow: '0 10px 30px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0'}}>
                  <div className="sora" style={{fontWeight: 800, fontSize: '20px', color: '#0f172a', marginBottom: '8px'}}>+ 95 more keywords in the full report</div>
                  <div style={{fontSize: '14px', color: '#64748b', marginBottom: '20px'}}>Organised into 10 categories — 10 per category, all filtered to niche + performing</div>
                  <a href="#aug-email" onClick={scrollToForm} className="sora" style={{background: 'linear-gradient(135deg, #1a56db, #1e3a8a)', color: 'white', fontWeight: 800, fontSize: '15px', padding: '14px 32px', borderRadius: '12px', textDecoration: 'none', display: 'inline-block', boxShadow: '0 4px 16px rgba(26,86,219,0.3)'}}>
                    Get the Full Report Free →
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Capped score footnote */}
          <div style={{padding: '12px 32px', background: '#f8fafc', borderTop: '1px solid #e2e8f0'}}>
            <div style={{fontSize: '11px', color: '#94a3b8'}}>* Score capped at 100 for display. Actual Golden Score may be higher.</div>
          </div>
        </div>
      </div>

      {/* APP PROMO BANNER — compact */}
      <div className="aug-app-promo">
        <div style={{background: 'linear-gradient(135deg, #1e3a8a, #1a56db)', borderRadius: '16px', padding: '20px 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '20px', flexWrap: 'wrap', boxShadow: '0 10px 40px rgba(26,86,219,0.2)'}}>
          <div style={{flex: 1, minWidth: '260px'}}>
            <div style={{fontSize: '11px', fontWeight: 700, color: '#93c5fd', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px'}}>💡 Did you know?</div>
            <div className="sora" style={{fontWeight: 800, fontSize: '17px', color: 'white', lineHeight: 1.3}}>
              Our app lets you search all 129,000+ keywords — rank tracking, competitor analysis & listing optimisation in one place.
            </div>
          </div>
          <div style={{display: 'flex', gap: '8px', flexShrink: 0, flexWrap: 'wrap'}}>
            {[['100', 'Keywords'], ['129k+', 'Analysed'], ['500+', '👥 Sellers'], ['175+', '⭐ Reviews']].map(([num, label]) => (
              <div key={label} style={{textAlign: 'center', background: 'rgba(255,255,255,0.08)', borderRadius: '10px', padding: '10px 14px'}}>
                <div className="sora" style={{fontWeight: 900, fontSize: '20px', color: '#fbbf24', lineHeight: 1}}>{num}</div>
                <div style={{fontSize: '10px', color: '#93c5fd', marginTop: '3px', textTransform: 'uppercase', whiteSpace: 'nowrap'}}>{label}</div>
              </div>
            ))}
          </div>
          <div style={{display: 'flex', gap: '10px', flexShrink: 0, flexWrap: 'wrap'}}>
            <Link to="/app" style={{background: 'white', color: '#1e40af', fontFamily: 'Sora, sans-serif', fontWeight: 800, fontSize: '13px', padding: '10px 20px', borderRadius: '12px', textDecoration: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.15)'}}>🚀 Try Free →</Link>
            <a href="https://www.teacherspayteachers.com/Product/TPT-Seller-SEO-Listing-Optimizer-App-Create-Titles-Descriptions-SEO-Ranking-13645689" target="_blank" rel="noopener noreferrer" style={{background: '#fbbf24', color: '#111827', fontFamily: 'Sora, sans-serif', fontWeight: 800, fontSize: '12px', padding: '10px 16px', borderRadius: '12px', textDecoration: 'none'}}>Buy Lifetime on TPT →</a>
          </div>
        </div>
      </div>

      {/* FOOTER */}
      <footer style={{background: 'white', borderTop: '1px solid #e2e8f0', textAlign: 'center', padding: '32px 24px', fontSize: '13px', color: '#64748b'}}>
        <p>
          <a href="https://mrjoelstptgenerator.com" style={{color: '#1a56db', textDecoration: 'none'}}>mrjoelstptgenerator.com</a> &nbsp;·&nbsp;
          <Link to="/app" style={{color: '#1a56db', textDecoration: 'none'}}>Try the App Free</Link> &nbsp;·&nbsp;
          <a href="https://www.teacherspayteachers.com/store/mr-joels-math-science-store" style={{color: '#1a56db', textDecoration: 'none'}}>My TPT Store</a>
        </p>
        <p style={{marginTop: '8px'}}>© 2026 Mr Joel's Seller Tools · Data analysed August 5th 2026 · Not affiliated with Teachers Pay Teachers.</p>
      </footer>

    </div>
  );
};

export default August2026;